/**
 * Route guards: `ProtectedRoute` for any signed-in user, `AdminRoute` for admins
 * (an owner and a developer are admins too: `isAdminRole`), `DeveloperRoute`
 * for developers (`isDeveloper`). While a developer views the dashboard as
 * someone else, `/auth/me` answers with the viewed role, so the guards follow
 * it, and the developer pages stay shut, also in a view of another developer.
 *
 * Only a 401/403 from `/auth/me` sends the user to /login. Any other failure
 * (the backend restarting under `make dev`, a 502 from the proxy, Supabase
 * down) is temporary: the guard shows "Opnieuw verbinden…" while `useMe` keeps
 * re-checking, and the page comes back by itself once the backend answers.
 *
 * These only shape the UX; the backend enforces every rule again.
 */

import { Loader2 } from "lucide-react";
import { createContext, useContext } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, Outlet } from "react-router";

import { isAuthError, useMe, type UserProfile } from "@/lib/api";
import { logger } from "@/lib/logger";
import { isAdminRole, isDeveloper } from "@/lib/roles";

/** The signed-in profile, inside a session gate; null outside one. */
const SessionContext = createContext<UserProfile | null>(null);

export function ProtectedRoute() {
  return <SessionGate />;
}

/**
 * Admins only. Nested inside `<ProtectedRoute>` (as the admin pages are, under
 * the app shell) it only checks the role: the outer gate already handles
 * sign-in, loading and reconnecting, and a second gate would show a second
 * "Reconnecting…" banner. On its own it is a full session gate.
 */
export function AdminRoute() {
  const me = useContext(SessionContext);
  if (!me) {
    return <SessionGate adminOnly />;
  }
  if (!isAdminRole(me.role)) {
    logger.debug("[auth] not an admin → /home");
    return <Navigate to="/home" replace />;
  }
  return <Outlet />;
}

/**
 * Developers only, nested inside `<ProtectedRoute>` as `<AdminRoute>` is:
 * anyone else goes to Home, and so does a developer viewing the dashboard as
 * someone else.
 */
export function DeveloperRoute() {
  const me = useContext(SessionContext);
  if (!me) {
    return <SessionGate developerOnly />;
  }
  if (!opensDeveloperPages(me)) {
    logger.debug("[auth] not a developer, or viewing as someone → /home");
    return <Navigate to="/home" replace />;
  }
  return <Outlet />;
}

/** Whether the developer pages open: for a developer, never while a view is on. */
function opensDeveloperPages(me: UserProfile): boolean {
  return isDeveloper(me.role) && !me.view_as;
}

interface SessionGateProps {
  /** Admins only: anyone else goes to Home. */
  adminOnly?: boolean;
  /** Developers only, not while viewing as someone: anyone else goes to Home. */
  developerOnly?: boolean;
}

function SessionGate({ adminOnly = false, developerOnly = false }: SessionGateProps) {
  const { data: me, error, failureCount, errorUpdateCount } = useMe();

  if (isAuthError(error)) {
    logger.debug("[auth] session refused → /login");
    return <Navigate to="/login" replace />;
  }

  if (!me) {
    // Nothing to show yet: the first check is still running, or it failed and
    // `useMe` is retrying.
    return failureCount > 0 || errorUpdateCount > 0 ? <Reconnecting /> : <FullPageLoader />;
  }

  if ((adminOnly && !isAdminRole(me.role)) || (developerOnly && !opensDeveloperPages(me))) {
    return <Navigate to="/home" replace />;
  }

  return (
    <SessionContext value={me}>
      {/* A re-check failed: keep the page (the last profile is still valid) and say so. */}
      {error ? <ReconnectingBanner /> : null}
      <Outlet context={{ me }} />
    </SessionContext>
  );
}

function FullPageLoader() {
  const { t } = useTranslation();
  return (
    <div className="flex min-h-screen items-center justify-center">
      <Loader2 className="text-muted h-6 w-6 animate-spin" aria-label={t("common.loading")} />
    </div>
  );
}

function Reconnecting() {
  const { t } = useTranslation();
  return (
    <div
      role="status"
      className="flex min-h-screen flex-col items-center justify-center gap-3 px-4 text-center"
    >
      <Loader2 className="text-muted h-6 w-6 animate-spin" aria-hidden="true" />
      <p className="text-foreground font-medium">{t("session.reconnecting")}</p>
      <p className="text-muted text-sm">{t("session.serverDown")}</p>
    </div>
  );
}

function ReconnectingBanner() {
  const { t } = useTranslation();
  return (
    <div
      role="status"
      className="border-border bg-card text-foreground fixed bottom-4 left-1/2 z-50 flex -translate-x-1/2 items-center gap-2 rounded-md border px-4 py-2 text-sm shadow-md"
    >
      <Loader2 className="text-muted h-4 w-4 animate-spin" aria-hidden="true" />
      {t("session.reconnecting")}
    </div>
  );
}
